import { useCallback, useEffect, useRef } from 'react';

/** Sliding-window length used to measure the refetch rate. */
export const REFETCH_LOOP_WINDOW_MS = 5000;

/**
 * Calls within the window that trip the guard. 30 calls in 5s (6/s
 * sustained) is beyond real interaction — list refetches are click-paced
 * (filter apply, pagination, sort; search applies on submit, not per
 * keystroke) — but well within reach of a render-loop refetch cycle, even
 * one paced by network round-trips rather than raw render speed.
 */
export const REFETCH_LOOP_MAX_CALLS = 30;

/**
 * Circuit breaker for refetch-on-state-change effects.
 *
 * The list pages refetch manually from a `useEffect` keyed on their query
 * options. If any upstream input regains a new identity every render (an
 * inline object literal, an unmemoised map) while some effect also triggers
 * its own re-render, that effect fires in a loop and hammers the BFF with
 * one aborted request per cycle — PR #350's saved-report bug reached prod
 * exactly this way, with nothing in dev, tests, or runtime to flag it.
 *
 * Wrapping the refetch callback with this guard converts that failure mode
 * from "unbounded failing requests for as long as the tab is open" into
 * "requests stop and a console.error names the page". The guard:
 *
 * - passes calls through while the rate is plausible for real interaction;
 * - trips once the rate exceeds {@link REFETCH_LOOP_MAX_CALLS} per
 *   {@link REFETCH_LOOP_WINDOW_MS}, swallowing further refetches and
 *   logging a single console.error naming `label`;
 * - in development and test builds it then THROWS, which fatally unmounts
 *   the looping tree — a refetch loop in dev is unmissable, and a test
 *   that provokes one fails fast instead of spinning until the runner is
 *   killed (the loop starves jsdom's timers, so nothing else can stop it);
 * - in production it never throws — requests stay suspended while a
 *   trailing timer waits for the call rate to drain, then runs one
 *   recovery refetch and re-arms. A false trip (e.g. pathological
 *   pagination mashing) therefore costs a few seconds of staleness at
 *   worst, never a permanently stale page.
 *
 * In production, tripping does not stop the underlying render loop (that
 * bug still needs fixing — see the referential-stability contract on
 * useReports' filterStateLookups), but it caps the blast radius to zero
 * network calls and makes the defect loud instead of silent.
 */
export const useRefetchLoopGuard = (
  refetch: () => void,
  label: string,
  options?: {
    /** Throw on trip. Defaults to true outside production builds. */
    throwOnTrip?: boolean;
  }
): (() => void) => {
  const throwOnTrip = options?.throwOnTrip ?? process.env.NODE_ENV !== 'production';
  const refetchRef = useRef(refetch);
  const callTimesRef = useRef<number[]>([]);
  const trippedRef = useRef(false);
  const recoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Latest-ref assignment happens in an effect (not during render) for
  // concurrent-render safety. This hook's effect is registered before any
  // consumer effect that calls the guarded function, so the ref is always
  // current by the time it is invoked.
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(
    () => () => {
      if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    },
    []
  );

  const pruneWindow = useCallback((now: number) => {
    const callTimes = callTimesRef.current;
    while (callTimes.length > 0 && now - callTimes[0] > REFETCH_LOOP_WINDOW_MS) {
      callTimes.shift();
    }
    return callTimes;
  }, []);

  // While tripped, periodically check whether the storm has passed; once it
  // has, run a single recovery refetch so the last swallowed state change
  // still lands, and re-arm the guard.
  const scheduleRecovery = useCallback(
    (label_: string) => {
      if (recoveryTimerRef.current) return;
      recoveryTimerRef.current = setTimeout(() => {
        recoveryTimerRef.current = null;
        // A guarded call may have already resumed normal operation while
        // this timer was pending — nothing left to recover.
        if (!trippedRef.current) return;
        const now = performance.now();
        if (pruneWindow(now).length > REFETCH_LOOP_MAX_CALLS) {
          scheduleRecovery(label_);
          return;
        }
        trippedRef.current = false;
        console.warn(`[refetch-loop-guard] ${label_}: refetch rate back to normal — resuming.`);
        callTimesRef.current.push(now);
        refetchRef.current();
      }, REFETCH_LOOP_WINDOW_MS);
    },
    [pruneWindow]
  );

  return useCallback(() => {
    const now = performance.now();
    const callTimes = pruneWindow(now);
    callTimes.push(now);

    if (callTimes.length > REFETCH_LOOP_MAX_CALLS) {
      const message =
        `[refetch-loop-guard] ${label}: ${callTimes.length} refetch triggers inside ` +
        `${REFETCH_LOOP_WINDOW_MS}ms — refetching suspended. A refetch effect is almost ` +
        `certainly re-firing every render via an unstable dependency (inline object/array ` +
        `literal or unmemoised value). See PR #350 and the refetch-loop section of ` +
        `frontend-rebuild-resources/coding-guidelines.md.`;
      if (!trippedRef.current) {
        trippedRef.current = true;
        console.error(message);
      }
      if (throwOnTrip) throw new Error(message);
      scheduleRecovery(label);
      return;
    }

    if (trippedRef.current) {
      trippedRef.current = false;
      if (recoveryTimerRef.current) {
        clearTimeout(recoveryTimerRef.current);
        recoveryTimerRef.current = null;
      }
      console.warn(`[refetch-loop-guard] ${label}: refetch rate back to normal — resuming.`);
    }
    refetchRef.current();
  }, [label, throwOnTrip, pruneWindow, scheduleRecovery]);
};
